/**
 * Formula Token Utilities
 * 
 * Stable, ID-based token system for board spec formulas.
 * Instead of fragile slugified names (which break on special characters
 * like & ' $ and can collide), we use unique ID-based tokens.
 *
 * Token format:
 *   ch_<channelId>   — channel reference
 *   jn_<journalId>   — journal reference
 *   en_<entityId>    — entity reference (including bridge roots)
 *   all              — global / parent-scoped "all" keyword
 *   revenues|expenses|net — metric categories
 *
 * Example formula: ch_abc123.en_def456.revenues + jn_ghi789.all.net
 */

// --- Token Prefixes ---
export const TOKEN_PREFIX = {
    CHANNEL: 'ch_',
    JOURNAL: 'jn_',
    ENTITY: 'en_',
} as const;

// --- Category constants (kept from old system) ---
export const CATEGORIES = ['revenues', 'expenses', 'net'] as const;
export const ALL_CATEGORIES = ['revenues', 'revenue', 'expenses', 'expense', 'net'] as const;
export const OPERATORS = ['+', '-', '*', '/', '(', ')'] as const;

// --- Token Builders ---
export function channelToken(id: string): string {
    return `${TOKEN_PREFIX.CHANNEL}${id}`;
}
export function journalToken(id: string): string {
    return `${TOKEN_PREFIX.JOURNAL}${id}`;
}
export function entityToken(id: string): string {
    return `${TOKEN_PREFIX.ENTITY}${id}`;
}

// --- Token Parsers ---
export function isChannelToken(token: string): boolean {
    return token.startsWith(TOKEN_PREFIX.CHANNEL);
}
export function isJournalToken(token: string): boolean {
    return token.startsWith(TOKEN_PREFIX.JOURNAL);
}
export function isEntityToken(token: string): boolean {
    return token.startsWith(TOKEN_PREFIX.ENTITY);
}

export function parseTokenId(token: string): { type: 'channel' | 'journal' | 'entity' | 'unknown'; id: string } {
    if (isChannelToken(token)) return { type: 'channel', id: token.slice(TOKEN_PREFIX.CHANNEL.length) };
    if (isJournalToken(token)) return { type: 'journal', id: token.slice(TOKEN_PREFIX.JOURNAL.length) };
    if (isEntityToken(token)) return { type: 'entity', id: token.slice(TOKEN_PREFIX.ENTITY.length) };
    return { type: 'unknown', id: token };
}

// --- Token Regex ---
// Matches ID-based tokens: ch_..., jn_..., en_... or keywords like 'all', 'revenues' etc.
// Token chars: alphanumeric plus underscore (MongoDB IDs are hex, so this covers them)
export const TOKEN_CHAR_REGEX = /[a-zA-Z0-9_]/;
// A token segment: starts with a letter or underscore, followed by alphanumeric/underscores
export const TOKEN_SEGMENT_REGEX = /[a-zA-Z_][a-zA-Z0-9_]*/;

/**
 * Check if a token segment is a metric category
 */
export function isCategory(seg: string): boolean {
    return (ALL_CATEGORIES as readonly string[]).includes(seg.toLowerCase());
}

/**
 * Check if a token segment is the 'all' keyword
 */
export function isAllKeyword(seg: string): boolean {
    return seg.toLowerCase() === 'all';
}

/**
 * Check if a string is an ID-based token (has a known prefix)
 */
export function isIdToken(seg: string): boolean {
    return isChannelToken(seg) || isJournalToken(seg) || isEntityToken(seg);
}
